import { observer } from 'mobx-react-lite'
import { useRef, useState } from 'react'
import classNames from 'classnames'
import {
  CalendarDropdown,
  FullPageWrapper,
  Typography,
  SpinnerLoader,
  Tooltip,
  Icon,
  type ActionItem,
  Button,
} from 'shared/ui'
import { useElementWidth } from 'shared/hooks'
import { MESSAGING_METRIC_COLORS } from 'entities/Analytics/lib/constants'
import { BreakdownFilter } from 'pages/analytics/ui/BreakdownFilter/BreakdownFilter'
import {
  MessagesAnalyticsPageProvider,
  useMessagesAnalyticsPageContext,
} from 'pages/analytics/pages/messages/context/messagesAnalyticsPageContext'
import { AnalyticsHeader, TrendLabel, AnalyticsEmptyState } from 'pages/analytics/ui'
import { ChartFilter } from 'pages/analytics/ui/ChartFilter'
import { MessagesLineChart, MessagesBarChart } from 'pages/analytics/pages/messages/ui/Chart'
import { MessagesComparisonTable } from 'pages/analytics/pages/messages/ui/ComparisonTable'
import styles from './styles.module.scss'

const MessagesAnalyticsPageContent = observer(() => {
  const {
    calendarFilterStore,
    breakdownFilterStore,
    chartFilterStore,
    chartDataStore,
    pageLoading,
    chartLoading,
    openExportModal,
    comparisonTableStore,
  } = useMessagesAnalyticsPageContext()
  const { clearAllFilters, hasAppliedFilters } = breakdownFilterStore
  const { getStatisticByKey, hasStatistic } = chartDataStore

  const [hiddenMetrics, setHiddenMetrics] = useState<Set<string>>(new Set())
  const filtersRowRef = useRef<HTMLDivElement | null>(null)
  const filtersRowWidth = useElementWidth(filtersRowRef)

  const handleToggleMetric = (metricKey: string) => {
    setHiddenMetrics((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(metricKey)) {
        newSet.delete(metricKey)
      } else {
        newSet.add(metricKey)
      }
      return newSet
    })
  }

  const totalInboundStat = getStatisticByKey('total_inbound')
  const totalOutboundStat = getStatisticByKey('total_outbound')
  const deliveredStat = getStatisticByKey('delivered')
  const undeliveredStat = getStatisticByKey('undelivered')
  const carrierViolationsStat = getStatisticByKey('carrier_violations')
  const optoutsStat = getStatisticByKey('optouts')
  const totalRvmStat = getStatisticByKey('total_rvm')
  const deliveredRvmStat = getStatisticByKey('delivered_rvm')
  const undeliveredRvmStat = getStatisticByKey('undelivered_rvm')

  const exportActions: ActionItem[] = [
    {
      text: 'Export data',
      icon: 'download',
      onAction: openExportModal,
    },
  ]

  const renderContent = () => {
    if (pageLoading) {
      return (
        <div className={styles.fullPageContainer}>
          <SpinnerLoader ariaLabel={'MessagesAnalyticsPage'} />
        </div>
      )
    }

    if (!hasStatistic) {
      return (
        <div className={styles.fullPageContainer}>
          <AnalyticsEmptyState />
        </div>
      )
    }

    return (
      <>
        <div className={styles.kpiSection}>
          <div
            className={classNames(styles.kpiCard, styles.kpiTotalInbound)}
            onClick={() => handleToggleMetric('total_inbound')}
          >
            <div
              className={styles.kpiColorBar}
              style={{
                backgroundColor: hiddenMetrics.has('total_inbound')
                  ? 'var(--gray-10)'
                  : MESSAGING_METRIC_COLORS.total_inbound,
              }}
            ></div>
            <div className={styles.kpiCardContent}>
              <div className={styles.kpiCardHeader}>
                <Typography
                  variant={'body-rg-regular'}
                  color={'tertiary'}
                  ariaLabel={'TotalInboundTitle'}
                >
                  Total inbound
                </Typography>
                <Tooltip label='The total number of inbound messages' placement='top'>
                  <Icon icon='question' color='var(--gray-40)' />
                </Tooltip>
              </div>
              <Typography
                variant={'heading-lg'}
                color={hiddenMetrics.has('total_inbound') ? 'tertiary' : 'initial'}
                ariaLabel={'TotalInboundValue'}
              >
                {totalInboundStat?.value?.toLocaleString() || '0'}
              </Typography>
              {totalInboundStat?.trend && (
                <TrendLabel trend={totalInboundStat.trend} displayMode='percent' />
              )}
            </div>
          </div>

          <div
            className={classNames(styles.kpiCard, styles.kpiTotalOutbound)}
            onClick={() => handleToggleMetric('total_outbound')}
          >
            <div
              className={styles.kpiColorBar}
              style={{
                backgroundColor: hiddenMetrics.has('total_outbound')
                  ? 'var(--gray-10)'
                  : MESSAGING_METRIC_COLORS.total_outbound,
              }}
            ></div>
            <div className={styles.kpiCardContent}>
              <div className={styles.kpiCardHeader}>
                <Typography
                  variant={'body-rg-regular'}
                  color={'tertiary'}
                  ariaLabel={'TotalOutboundTitle'}
                >
                  Total outbound
                </Typography>
                <Tooltip label='The total number of outbound messages' placement='top'>
                  <Icon icon='question' color='var(--gray-40)' />
                </Tooltip>
              </div>
              <div className={styles.kpiCardMainRow}>
                <Typography
                  variant={'heading-lg'}
                  color={hiddenMetrics.has('total_outbound') ? 'tertiary' : 'initial'}
                  ariaLabel={'TotalOutboundValue'}
                >
                  {totalOutboundStat?.value?.toLocaleString() || '0'}
                </Typography>
                {totalOutboundStat?.trend && (
                  <TrendLabel trend={totalOutboundStat.trend} displayMode='percent' />
                )}
              </div>
            </div>
          </div>

          <div className={classNames(styles.kpiSubMetricsGrid, styles.kpiDeliveryStats)}>
            <div className={styles.kpiCard} onClick={() => handleToggleMetric('delivered')}>
              <div
                className={styles.kpiColorBar}
                style={{
                  backgroundColor: hiddenMetrics.has('delivered')
                    ? 'var(--gray-10)'
                    : MESSAGING_METRIC_COLORS.delivered,
                }}
              ></div>
              <div className={styles.kpiCardContent}>
                <div className={styles.kpiCardHeader}>
                  <Typography
                    variant={'body-sm-regular'}
                    color={'tertiary'}
                    ariaLabel={'DeliveryRateTitle'}
                  >
                    Delivery rate
                  </Typography>
                  <Tooltip label='The percentage of delivered messages' placement='top'>
                    <Icon icon='question' color='var(--gray-40)' />
                  </Tooltip>
                </div>
                <Typography
                  variant={'heading-sm'}
                  color={hiddenMetrics.has('delivered') ? 'tertiary' : 'initial'}
                  ariaLabel={'DeliveryRateValue'}
                >
                  {deliveredStat?.value || 0}%
                </Typography>
              </div>
            </div>

            <div className={styles.kpiCard} onClick={() => handleToggleMetric('undelivered')}>
              <div
                className={styles.kpiColorBar}
                style={{
                  backgroundColor: hiddenMetrics.has('undelivered')
                    ? 'var(--gray-10)'
                    : MESSAGING_METRIC_COLORS.undelivered,
                }}
              ></div>
              <div className={styles.kpiCardContent}>
                <div className={styles.kpiCardHeader}>
                  <Typography
                    variant={'body-sm-regular'}
                    color={'tertiary'}
                    ariaLabel={'ErrorRateTitle'}
                  >
                    Error rate
                  </Typography>
                  <Tooltip label='The percentage of undelivered messages' placement='top'>
                    <Icon icon='question' color='var(--gray-40)' />
                  </Tooltip>
                </div>
                <Typography
                  variant={'heading-sm'}
                  color={hiddenMetrics.has('undelivered') ? 'tertiary' : 'initial'}
                  ariaLabel={'ErrorRateValue'}
                >
                  {undeliveredStat?.value || 0}%
                </Typography>
              </div>
            </div>

            <div
              className={styles.kpiCard}
              onClick={() => handleToggleMetric('carrier_violations')}
            >
              <div
                className={styles.kpiColorBar}
                style={{
                  backgroundColor: hiddenMetrics.has('carrier_violations')
                    ? 'var(--gray-10)'
                    : MESSAGING_METRIC_COLORS.carrier_violations,
                }}
              ></div>
              <div className={styles.kpiCardContent}>
                <div className={styles.kpiCardHeader}>
                  <Typography
                    variant={'body-sm-regular'}
                    color={'tertiary'}
                    ariaLabel={'SpamRateTitle'}
                  >
                    Spam rate
                  </Typography>
                  <Tooltip
                    label='The percentage of undelivered messages that were flagged for carrier violations'
                    placement='top'
                  >
                    <Icon icon='question' color='var(--gray-40)' />
                  </Tooltip>
                </div>
                <Typography
                  variant={'heading-sm'}
                  color={hiddenMetrics.has('carrier_violations') ? 'tertiary' : 'initial'}
                  ariaLabel={'SpamRateValue'}
                >
                  {carrierViolationsStat?.value || 0}%
                </Typography>
              </div>
            </div>
          </div>

          <div
            className={classNames(styles.kpiCard, styles.kpiOptedOut)}
            onClick={() => handleToggleMetric('optouts')}
          >
            <div
              className={styles.kpiColorBar}
              style={{
                backgroundColor: hiddenMetrics.has('optouts')
                  ? 'var(--gray-10)'
                  : MESSAGING_METRIC_COLORS.optouts,
              }}
            ></div>
            <div className={styles.kpiCardContent}>
              <div className={styles.kpiCardHeader}>
                <Typography
                  variant={'body-rg-regular'}
                  color={'tertiary'}
                  ariaLabel={'OptedOutTitle'}
                >
                  Opted-out
                </Typography>
              </div>
              <Typography
                variant={'heading-lg'}
                color={hiddenMetrics.has('optouts') ? 'tertiary' : 'initial'}
                ariaLabel={'OptedOutValue'}
              >
                {optoutsStat?.value?.toLocaleString() || '0'}
              </Typography>
              {optoutsStat?.trend && <TrendLabel trend={optoutsStat.trend} displayMode='percent' />}
            </div>
          </div>

          <div
            className={classNames(styles.kpiCard, styles.kpiRvm)}
            onClick={() => handleToggleMetric('total_rvm')}
          >
            <div
              className={styles.kpiColorBar}
              style={{
                backgroundColor: hiddenMetrics.has('total_rvm')
                  ? 'var(--gray-10)'
                  : MESSAGING_METRIC_COLORS.total_rvm,
              }}
            ></div>
            <div className={styles.kpiCardContent}>
              <div className={styles.kpiCardHeader}>
                <Typography variant={'body-rg-regular'} color={'tertiary'} ariaLabel={'RvmTitle'}>
                  RVM
                </Typography>
              </div>
              <div className={styles.kpiCardMainRow}>
                <Typography
                  variant={'heading-lg'}
                  color={hiddenMetrics.has('total_rvm') ? 'tertiary' : 'initial'}
                  ariaLabel={'RvmValue'}
                >
                  {totalRvmStat?.value?.toLocaleString() || '0'}
                </Typography>
                {totalRvmStat?.trend && (
                  <TrendLabel trend={totalRvmStat.trend} displayMode='percent' />
                )}
              </div>
            </div>
          </div>

          <div
            className={classNames(
              styles.kpiSubMetricsGrid,
              styles.kpiSubMetricsGrid2Cols,
              styles.kpiRvmStats
            )}
          >
            <div className={styles.kpiCard} onClick={() => handleToggleMetric('delivered_rvm')}>
              <div
                className={styles.kpiColorBar}
                style={{
                  backgroundColor: hiddenMetrics.has('delivered_rvm')
                    ? 'var(--gray-10)'
                    : MESSAGING_METRIC_COLORS.delivered_rvm,
                }}
              ></div>
              <div className={styles.kpiCardContent}>
                <div className={styles.kpiCardHeader}>
                  <Typography
                    variant={'body-sm-regular'}
                    color={'tertiary'}
                    ariaLabel={'DeliveredTitle'}
                  >
                    Delivered
                  </Typography>
                </div>
                <Typography
                  variant={'heading-sm'}
                  color={hiddenMetrics.has('delivered_rvm') ? 'tertiary' : 'initial'}
                  ariaLabel={'DeliveredValue'}
                >
                  {deliveredRvmStat?.value?.toLocaleString() || '0'}
                </Typography>
              </div>
            </div>

            <div
              className={classNames(styles.kpiCard, styles.kpiUndeliveredRvm)}
              onClick={() => handleToggleMetric('undelivered_rvm')}
            >
              <div
                className={styles.kpiColorBar}
                style={{
                  backgroundColor: hiddenMetrics.has('undelivered_rvm')
                    ? 'var(--gray-10)'
                    : MESSAGING_METRIC_COLORS.undelivered_rvm,
                }}
              ></div>
              <div className={styles.kpiCardContent}>
                <div className={styles.kpiCardHeader}>
                  <Typography
                    variant={'body-sm-regular'}
                    color={'tertiary'}
                    ariaLabel={'UndeliveredTitle'}
                  >
                    Undelivered
                  </Typography>
                </div>
                <Typography
                  variant={'heading-sm'}
                  color={hiddenMetrics.has('undelivered_rvm') ? 'tertiary' : 'initial'}
                  ariaLabel={'UndeliveredValue'}
                >
                  {undeliveredRvmStat?.value?.toLocaleString() || '0'}
                </Typography>
              </div>
            </div>
          </div>
        </div>

        <div className={styles.chartSection}>
          <div className={styles.chartHeader}>
            <ChartFilter store={chartFilterStore} />
          </div>

          <div className={styles.chartArea}>
            {chartLoading ? (
              <SpinnerLoader ariaLabel={'MessagesAnalyticsPage'} />
            ) : (
              <>
                {chartFilterStore.chartType === 'line' ? (
                  <MessagesLineChart
                    chartDataStore={chartDataStore}
                    hiddenMetrics={hiddenMetrics}
                  />
                ) : (
                  <MessagesBarChart chartDataStore={chartDataStore} hiddenMetrics={hiddenMetrics} />
                )}
              </>
            )}
          </div>
        </div>

        {hasAppliedFilters && (
          <div className={styles.tableSection}>
            <MessagesComparisonTable store={comparisonTableStore} />
          </div>
        )}
      </>
    )
  }

  return (
    <FullPageWrapper className={styles.page}>
      <AnalyticsHeader title='Messages' actions={exportActions} />

      <div className={styles.filtersRow} ref={filtersRowRef}>
        <CalendarDropdown store={calendarFilterStore} />
        <BreakdownFilter store={breakdownFilterStore} parentWidth={filtersRowWidth} />
        {hasAppliedFilters && (
          <Button
            contained='secondary'
            color={'var(--content-primary-secondary)'}
            typeBtn='text'
            text='Clear all'
            onClick={clearAllFilters}
          />
        )}
      </div>

      {renderContent()}
    </FullPageWrapper>
  )
})

export const MessagesAnalyticsPage = () => (
  <MessagesAnalyticsPageProvider>
    <MessagesAnalyticsPageContent />
  </MessagesAnalyticsPageProvider>
)
